import {
  alpha,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  Link,
  List,
  ListItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Close as CloseIcon } from "@mui/icons-material";
import { LoadingButton } from "~/components/loading-button";
import { ExceptionMutationAction, ScoreRating } from "~/operations";
import { useColorMode } from "~/providers/color-mode";
import { ExceptionTarget } from "~/components/exceptions/types";
import { targetLabelMap } from "~/components/exceptions/constants";
import {
  ExceptionFormValidUntil,
  useSetExceptionModal,
} from "./hooks/useSetExceptionDialog";
import { docsHref } from "~/lib/docs-href";
import { cloneElement, useEffect } from "react";
import { ImpactUpdated } from "../impact/Updated/impact-updated";
import { Clamp } from "../clamp/clamp";
import { pluralize } from "~/lib/pluralize";
import { useScope } from "~/hooks/useScope";
import { DialogProps } from "@toolpad/core";
import { EntityInfoAsset } from "~/components/FirewatchPage";
import { Flex } from "../Flex";
import { SelectedEntity } from "~/hooks/useSelection";

export type ExceptionFormInput = {
  action: ExceptionMutationAction;
  validUntil: ExceptionFormValidUntil;
  customValidUntil: number;
  scope: string;
  justification: string;
};

export type SelectedEntityWithFindingData = SelectedEntity & {
  rating?: ScoreRating;
  title: string;
  type?: "CheckFinding" | "CveFinding" | "AdvisoryFinding" | "GenericFinding";
};

export interface SetExceptionDialogPayload {
  onSave: (params: ExceptionFormInput) => Promise<void>;
  target: ExceptionTarget;
  selectedEntities?: SelectedEntityWithFindingData[];
  shouldListEntities?: boolean;
  targetData: {
    targetTitle?: string;
    onAsset?: EntityInfoAsset | null | undefined;
  };
}

export interface SetExceptionDialogProps
  extends DialogProps<SetExceptionDialogPayload> {}

export function SetExceptionDialog(props: SetExceptionDialogProps) {
  const { open, onClose } = props;
  const { mode } = useColorMode();
  const { activeScope, spaceScope } = useScope();

  const {
    onSave,
    target,
    selectedEntities,
    targetData,
    shouldListEntities = false,
  } = props.payload as SetExceptionDialogPayload;

  const {
    register,
    handleSubmit,
    formState,
    reset,
    control,
    setValue,
    watch,
    trigger,
  } = useForm<ExceptionFormInput>({
    mode: "onChange",
    defaultValues: {
      action: ExceptionMutationAction.RiskAccepted,
      validUntil: ExceptionFormValidUntil.Month6,
      scope: activeScope?.mrn || "",
      justification: "",
      customValidUntil: 0,
    },
  });

  // Update scope when data.onAsset changes
  useEffect(() => {
    if (!spaceScope || !targetData.onAsset) return;

    const newScope = Boolean(targetData.onAsset)
      ? targetData.onAsset?.mrn
      : spaceScope?.mrn;

    setValue("scope", newScope);
  }, [targetData.onAsset, setValue, open]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const {
    exceptionTypes,
    timeLimitOptions,
    form,
    handleClose,
    scopeOptions,
    isLoading,
  } = useSetExceptionModal({
    onSave,
    reset,
    onClose,
    onAsset: targetData?.onAsset,
  });

  const validUntilWatch = watch("validUntil");
  const isCustomDurationSelected =
    validUntilWatch === ExceptionFormValidUntil.Custom;

  useEffect(() => {
    trigger("customValidUntil");
  }, [validUntilWatch]);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      slotProps={{
        paper: {
          // @ts-ignore https://github.com/mui/material-ui/pull/32404#issuecomment-1105228783
          component: "form",
          onSubmit: handleSubmit(form.handleFormSubmit),
        },
      }}
      onClose={handleClose}
      data-testid="set-exception-dialog"
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 5,
          pb: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
          }}
        >
          Set an exception{" "}
          {targetData?.targetTitle ? `for ${targetData?.targetTitle}` : ""}
        </Typography>
        <IconButton
          onClick={handleClose}
          data-testid="close-exception-dialog"
          aria-label="Close dialog"
        >
          <CloseIcon sx={{ color: "text.secondary" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 5, pb: 2 }}>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Choose the kind of exception you want to request for this{" "}
          {targetLabelMap[target]}. Providing a justification will add context
          to the exception for easier review. To learn more about exceptions and
          their types,{" "}
          <Link
            href={docsHref("/platform/security/customize/overview/")}
            target="_blank"
            rel="noopener"
          >
            read the Mondoo Documentation
          </Link>
          .
        </Typography>
        <FormControl fullWidth sx={{ pl: "11px" }}>
          <Controller
            rules={{ required: true }}
            control={control}
            name="action"
            render={({ field }) => (
              <RadioGroup
                {...field}
                row
                sx={{
                  display: "flex",
                  gap: 3,
                  mb: 3,
                }}
              >
                {exceptionTypes.map((option) => {
                  const isSelected = option.value === field.value;

                  return (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={
                        <Radio
                          data-testid={`${option.value}-radio`}
                          sx={{ position: "absolute", opacity: 0 }}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            flexShrink: 0,
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 1,
                            border: "2px solid",
                            borderColor: "transparent",
                            height: "100%",
                            width: 164,
                            borderRadius: 1,
                            backgroundColor: "background.light",
                            ...(isSelected && {
                              borderColor: "primary.main",
                              backgroundColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.2),
                              boxShadow:
                                "0px 0px 40px 0px rgba(0, 0, 0, 0.33) inset",
                            }),
                          }}
                        >
                          {cloneElement(option.icon, {
                            sx: {
                              fontSize: 64,
                            },
                          })}
                          <Box sx={{ textAlign: "center" }}>
                            <Typography
                              sx={{ fontWeight: 700, lineHeight: "24px" }}
                            >
                              {option.label}
                            </Typography>
                            <Typography
                              sx={{
                                color: "text.secondary",
                                fontSize: 12,
                                lineHeight: "16px",
                                textAlign: "center",
                              }}
                            >
                              {option.tagline}
                            </Typography>
                          </Box>
                        </Box>
                      }
                      sx={{
                        display: "flex",
                        alignItems: "stretch",
                        mr: 1,
                        ".MuiFormControlLabel-label": { fontWeight: 700 },
                      }}
                    />
                  );
                })}
              </RadioGroup>
            )}
          />
        </FormControl>
        <FormControl fullWidth>
          <Typography
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            Time Limit
          </Typography>
          <Box sx={{ pl: "11px" }}>
            <Controller
              control={control}
              name="validUntil"
              render={({ field }) => (
                <RadioGroup {...field} row sx={{ mb: 3, gap: 1.25 }}>
                  {timeLimitOptions.map((option) => {
                    const isSelected = option.value === field.value;
                    const isCustomField =
                      option.value === ExceptionFormValidUntil.Custom;
                    return (
                      <>
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={
                            <Radio
                              sx={{ position: "absolute", opacity: 0 }}
                              data-testid={`${option.value}-radio`}
                              role="radio"
                            />
                          }
                          label={option.label}
                          sx={{
                            position: "relative",
                            py: 0.5,
                            px: 1,
                            ...(isCustomField ? { mr: 0 } : {}),
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, 0.2),
                            border: "1px solid",
                            borderColor: "transparent",
                            borderRadius: 1,
                            ...(isSelected && {
                              borderColor: "primary.main",
                              backgroundColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.4),
                            }),
                            "& .MuiFormControlLabel-label": {
                              fontWeight: 700,
                            },
                          }}
                        />
                        {isCustomField && (
                          <Flex>
                            <TextField
                              data-testid="exception-custom-duration"
                              type="number"
                              onFocus={() => {
                                setValue(
                                  "validUntil",
                                  ExceptionFormValidUntil.Custom,
                                );
                              }}
                              {...register(`customValidUntil`, {
                                valueAsNumber: true,
                                validate: (value) => {
                                  if (isCustomDurationSelected && value <= 0) {
                                    return "Number of days should be provided";
                                  }
                                  return true;
                                },
                              })}
                              slotProps={{
                                htmlInput: {
                                  min: 0,
                                  sx: { py: 1, px: 2, height: "18px" },
                                },
                              }}
                              variant="outlined"
                              sx={{
                                maxWidth: "72px",
                                boxSizing: "content-box",
                              }}
                              error={Boolean(
                                formState.errors?.customValidUntil,
                              )}
                            />
                            <Divider
                              orientation="vertical"
                              flexItem
                              sx={{ mx: 2 }}
                            />
                          </Flex>
                        )}
                      </>
                    );
                  })}
                </RadioGroup>
              )}
            />
          </Box>
        </FormControl>
        {targetData?.onAsset && (
          <FormControl fullWidth>
            <Typography
              sx={{
                fontWeight: 700,
                mb: 1,
              }}
            >
              Exception applies to
            </Typography>
            <Box sx={{ pl: "11px" }}>
              <Controller
                control={control}
                name="scope"
                render={({ field }) => (
                  <RadioGroup {...field} row sx={{ mb: 3, gap: 1.25 }}>
                    {scopeOptions.map((option) => {
                      const isSelected = option.value === field.value;

                      return (
                        <FormControlLabel
                          key={option.key}
                          value={option.value}
                          control={
                            <Radio sx={{ position: "absolute", opacity: 0 }} />
                          }
                          label={option.label}
                          sx={{
                            position: "relative",
                            py: 0.5,
                            px: 1,
                            backgroundColor: (theme) =>
                              alpha(theme.palette.primary.main, 0.2),
                            border: "1px solid",
                            borderColor: "transparent",
                            borderRadius: 1,
                            ...(isSelected && {
                              borderColor: "primary.main",
                              backgroundColor: (theme) =>
                                alpha(theme.palette.primary.main, 0.4),
                            }),
                            "& .MuiFormControlLabel-label": {
                              fontWeight: 700,
                            },
                          }}
                        />
                      );
                    })}
                  </RadioGroup>
                )}
              />
            </Box>
          </FormControl>
        )}
        <FormControl fullWidth>
          <Typography
            sx={{
              fontWeight: 700,
              mb: 1,
            }}
          >
            Justification{" "}
            <Typography component="span" color="text.secondary">
              (Limit 400 characters)
            </Typography>
          </Typography>
          <TextField
            placeholder="Provide a reason for your changes..."
            multiline
            rows={8}
            {...register("justification", {
              required: "Justification is required",
              maxLength: {
                value: 400,
                message: "Must be 400 characters or fewer",
              },
            })}
            error={Boolean(formState.errors.justification)}
            helperText={formState.errors.justification?.message}
            slotProps={{
              input: {
                sx: {
                  backgroundColor:
                    mode === "light" ? "common.white" : "common.black",
                  borderRadius: 1,
                },
              },
            }}
            data-testid="justification-input"
          />
        </FormControl>

        {shouldListEntities &&
          selectedEntities &&
          selectedEntities.length > 0 && (
            <Box sx={{ width: "100%", mt: 3 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography fontWeight={700}>Findings to include</Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedEntities.length}{" "}
                  {pluralize("Finding", selectedEntities.length)} Total
                </Typography>
              </Stack>
              <List
                disablePadding
                sx={{ "& .MuiListItem-root:not(:last-child)": { mb: 1 } }}
              >
                {selectedEntities.map((finding) => (
                  <Paper
                    component={ListItem}
                    key={finding.mrn}
                    sx={{ p: 2, backgroundColor: "background.light" }}
                  >
                    <Stack direction="row" gap={2}>
                      {finding.rating && (
                        <ImpactUpdated
                          impact={{
                            rating: finding.rating || ScoreRating.Unscored,
                          }}
                          sx={{ width: 97 }}
                        />
                      )}
                      <Clamp>
                        <Typography fontWeight={700}>
                          {finding.title}
                        </Typography>
                      </Clamp>
                    </Stack>
                  </Paper>
                ))}
              </List>
            </Box>
          )}
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", pb: 5 }}>
        <LoadingButton
          variant="contained"
          color="primary"
          type="submit"
          disabled={!formState.isValid}
          loading={isLoading}
          buttonText="Save Exception"
          data-test-id="save-exception-button"
        />
      </DialogActions>
    </Dialog>
  );
}
